import { toast } from '@postscript/components';
import { AnnouncementType } from 'components/admin/Announcements/utils';
import { HIDE_SDK_EMBED_BANNER } from 'components/admin/utils/feature-flags';
import { useBanners } from 'components/GlobalBanners/globalBanners';
import { usePSLabs } from 'controllers/contexts/labsFeatures';
import { useUser } from 'controllers/contexts/user';
import { useSdkAppExtensionStatus } from 'hooks/useSdkAppExtensionStatus';
import { useEffect, useRef, useState } from 'react';
import { BANNER_TYPES } from 'utils/events';

const THEME_APP_EXTENSION_VARIANT = {
  id: 'SDK_THEME_APP_EXTENSION_NOTIFICATION',
  bannerType: BANNER_TYPES.SDK_APP_EXTENSION_INSTALL,
  announcementType: undefined,
  bodyText: (
    <>
      Because of a theme change in Shopify, Postscript&apos;s acquisition tools
      and automation events are not active on your website. To fix this, turn on
      the Postscript app embed in your Shopify theme&apos;s settings.
    </>
  ),
  actionText: 'Turn On App Embed',
};

const LEGACY_APP_EXTENSION_VARIANT = {
  id: 'SDK_LEGACY_APP_EXTENSION_NOTIFICATION',
  bannerType: BANNER_TYPES.SDK_APP_EXTENSION_LEGACY_INSTALL,
  // Page-top only when no other emergency alert needs the single slot; otherwise the drawer.
  announcementType: AnnouncementType.EMERGENCY,
  bodyText: (
    <>
      An upcoming Shopify change may cause Postscript acquisition tools and
      automation events to stop working on your website. Turn on the Postscript
      app embed in the Shopify theme settings to keep them running.
    </>
  ),
  actionText: 'Turn On App Embed',
};

const SdkAppExtensionBanner = (): null => {
  const shownBannerIdRef = useRef<string | null>(null);
  const [hasFailed, setHasFailed] = useState(false);
  const { banners, addBanner, removeBanner } = useBanners();
  const { user } = useUser();
  const { hasInitialized: labsLoaded, hasLabsFlag } = usePSLabs();
  const isHidden = hasLabsFlag(HIDE_SDK_EMBED_BANNER);
  const hasOtherEmergency =
    banners.some(
      (banner) =>
        banner?.announcementType === AnnouncementType.EMERGENCY &&
        banner.id !== LEGACY_APP_EXTENSION_VARIANT.id,
    ) ||
    (user?.ps_announcements || []).some(
      (announcement) =>
        announcement.announcement_type === AnnouncementType.EMERGENCY,
    );
  const { data } = useSdkAppExtensionStatus({
    // Wait for labs flags so a hidden shop never flashes the banner or hits Shopify.
    enabled: !hasFailed && labsLoaded && !isHidden,
    refetchOnWindowFocus: 'always',
    onError: () => setHasFailed(true),
  });

  useEffect(() => {
    if (isHidden) {
      if (shownBannerIdRef.current) {
        removeBanner(shownBannerIdRef.current);
        shownBannerIdRef.current = null;
      }
      return;
    }

    if (!data) {
      return;
    }

    if (data.hasExtension === false) {
      const { id, bannerType, bodyText, actionText, announcementType } =
        data.usingThemeAppExtension
          ? THEME_APP_EXTENSION_VARIANT
          : LEGACY_APP_EXTENSION_VARIANT;
      const shownBannerId = shownBannerIdRef.current;
      if (shownBannerId) {
        removeBanner(shownBannerId);
      }
      shownBannerIdRef.current = id;
      addBanner({
        id,
        announcementType: hasOtherEmergency ? undefined : announcementType,
        data: {
          variant: 'warning',
          heading: 'Action Required',
          bodyText,
          primaryAction: {
            text: actionText,
            onClick: () => window.open(data.installUrl, '_blank'),
          },
        },
        eventTrackingOptions: { bannerType },
      });
    }

    if (data.hasExtension === true && shownBannerIdRef.current) {
      removeBanner(shownBannerIdRef.current);
      shownBannerIdRef.current = null;
      toast.success('Postscript app embed enabled');
    }
  }, [
    data?.hasExtension,
    data?.usingThemeAppExtension,
    hasOtherEmergency,
    isHidden,
  ]);

  return null;
};

export default SdkAppExtensionBanner;
